import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Plus, Trash2, Clock, MessageCircle } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PatientCombobox from "@/components/PatientCombobox";
import { toast } from "sonner";

const STATUS_META = {
  programada: { label: "Programada", color: "bg-[#EAE6DF] text-[#2A2F2C]" },
  completada: { label: "Completada", color: "bg-[#D1E0D7] text-[#2A2F2C]" },
  cancelada: { label: "Cancelada", color: "bg-[#E8D1D1] text-[#2A2F2C]" },
  no_show: { label: "No vino", color: "bg-[#F3E5D8] text-[#2A2F2C]" },
};

const GABINETE_COLOR = {
  "1": "border-l-[#4A5D4E]",
  "2": "border-l-[#C07F67]",
};

function ymd(d) {
  return d.toISOString().slice(0, 10);
}
function dayLabel(d) {
  return d.toLocaleDateString("es-ES", { weekday: "short", day: "numeric", month: "short" });
}
function dayLabelLong(d) {
  return d.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}
function monthLabel(d) {
  return d.toLocaleDateString("es-ES", { month: "long", year: "numeric" });
}
function timeLabel(s) {
  if (!s) return "";
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
}
function startOfWeek(d) {
  const dt = new Date(d);
  const day = (dt.getDay() + 6) % 7; // Monday=0
  dt.setDate(dt.getDate() - day);
  dt.setHours(0, 0, 0, 0);
  return dt;
}
function toLocalInput(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}
function startOfMonth(d) {
  const dt = new Date(d);
  dt.setDate(1);
  dt.setHours(0, 0, 0, 0);
  return dt;
}
function endOfMonth(d) {
  const dt = new Date(d);
  dt.setMonth(dt.getMonth() + 1);
  dt.setDate(0);
  dt.setHours(23, 59, 59, 999);
  return dt;
}
function startOfMonthGrid(d) {
  // Lunes anterior (o igual) al día 1
  return startOfWeek(startOfMonth(d));
}
function endOfMonthGrid(d) {
  // Domingo posterior (o igual) al último día → completa 5 o 6 semanas
  const last = endOfMonth(d);
  const day = (last.getDay() + 6) % 7;
  const r = new Date(last);
  r.setDate(last.getDate() + (6 - day));
  r.setHours(23, 59, 59, 999);
  return r;
}

function buildWhatsAppLink(phoneRaw, message) {
  if (!phoneRaw) return null;
  // Normaliza el teléfono: elimina espacios, guiones, paréntesis y prefijo "00"
  let phone = String(phoneRaw).replace(/[^\d+]/g, "");
  if (phone.startsWith("00")) phone = "+" + phone.slice(2);
  if (!phone.startsWith("+")) {
    // Si tiene 9 dígitos asumimos prefijo España
    if (phone.length === 9) phone = "+34" + phone;
  }
  phone = phone.replace("+", "");
  return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
}

export default function Appointments() {
  const { user, centers } = useAuth();
  const isReadOnly = false;
  const [appts, setAppts] = useState([]);
  const [patients, setPatients] = useState([]);
  const [users, setUsers] = useState([]);
  const [centerFilter, setCenterFilter] = useState("all");
  const [view, setView] = useState("week"); // 'day' | 'week' | 'month'
  const [currentDate, setCurrentDate] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [noShowTarget, setNoShowTarget] = useState(null);
  const [noShowReason, setNoShowReason] = useState("");
  const [notifyAfterCreate, setNotifyAfterCreate] = useState(true);
  const [editTarget, setEditTarget] = useState(null);
  const [avail, setAvail] = useState(null);
  const [form, setForm] = useState({
    center_id: "",
    employee_id: "",
    patient_id: "",
    title: "",
    gabinete: "1",
    start_time: new Date().toISOString().slice(0, 16),
    duration_minutes: 30,
    notes: "",
    status: "programada",
  });

  // Computa rango según vista
  const range = useMemo(() => {
    if (view === "day" || view === "cabinets") {
      const start = new Date(currentDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(end.getDate() + 1);
      return { start, end };
    }
    if (view === "month") {
      return { start: startOfMonthGrid(currentDate), end: endOfMonthGrid(currentDate) };
    }
    const ws = startOfWeek(currentDate);
    const we = new Date(ws);
    we.setDate(we.getDate() + 7);
    return { start: ws, end: we };
  }, [view, currentDate]);

  const load = async () => {
    const params = {
      date_from: ymd(range.start),
      date_to: ymd(range.end),
    };
    if (["admin", "center_admin"].includes(user?.role) && centerFilter !== "all") params.center_id = centerFilter;

    const [appts, patientList, usersData] = await Promise.all([
      api.get("/appointments", { params }).then((r) => r.data),
      api.get("/patients").then((r) => r.data),
      ["admin", "center_admin"].includes(user?.role)
        ? api.get("/users").then((r) => r.data)
        : Promise.resolve([user]),
    ]);
    setAppts(appts);
    setPatients(patientList);
    setUsers(usersData);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [centerFilter, view, currentDate]);

  // Disponibilidad de gabinetes/trabajadores para el tramo elegido
  useEffect(() => {
    if (!open || !form.start_time) {
      setAvail(null);
      return;
    }
    const cid = form.center_id || user?.center_id || centers[0]?.id;
    const d = new Date(form.start_time);
    if (!cid || isNaN(d.getTime())) return;
    const t = setTimeout(() => {
      api
        .get("/appointments/availability", {
          params: {
            center_id: cid,
            start_time: d.toISOString(),
            duration_minutes: parseInt(form.duration_minutes) || 30,
            ...(editTarget ? { exclude_id: editTarget.id } : {}),
          },
        })
        .then((r) => setAvail(r.data))
        .catch(() => setAvail(null));
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [open, form.start_time, form.duration_minutes, form.center_id, editTarget]);

  const goPrev = () => {
    const d = new Date(currentDate);
    if (view === "day" || view === "cabinets") d.setDate(d.getDate() - 1);
    else if (view === "week") d.setDate(d.getDate() - 7);
    else d.setMonth(d.getMonth() - 1);
    setCurrentDate(d);
  };
  const goNext = () => {
    const d = new Date(currentDate);
    if (view === "day" || view === "cabinets") d.setDate(d.getDate() + 1);
    else if (view === "week") d.setDate(d.getDate() + 7);
    else d.setMonth(d.getMonth() + 1);
    setCurrentDate(d);
  };
  const goToday = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    setCurrentDate(d);
  };

  const periodLabel = useMemo(() => {
    if (view === "day" || view === "cabinets") return dayLabelLong(currentDate);
    if (view === "month")
      return monthLabel(currentDate).replace(/^./, (c) => c.toUpperCase());
    const ws = startOfWeek(currentDate);
    const we = new Date(ws);
    we.setDate(we.getDate() + 6);
    return `${dayLabel(ws)} – ${dayLabel(we)}`;
  }, [view, currentDate]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        center_id: form.center_id || user?.center_id || centers[0]?.id,
        patient_id: form.patient_id || null,
        duration_minutes: parseInt(form.duration_minutes) || 30,
        start_time: new Date(form.start_time).toISOString(),
      };
      let data;
      if (editTarget) {
        const resp = await api.patch(`/appointments/${editTarget.id}`, payload);
        data = resp.data;
        toast.success("Cita actualizada");
      } else {
        const resp = await api.post("/appointments", payload);
        data = resp.data;
        toast.success("Cita creada");
      }
      // Aviso al paciente por WhatsApp (si está marcado y hay paciente con teléfono)
      if (notifyAfterCreate && payload.patient_id) {
        const p = patients.find((x) => x.id === payload.patient_id);
        if (p && p.phone) {
          const dt = new Date(payload.start_time);
          const fecha = dt.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });
          const hora = dt.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
          const cName = centers.find((c) => c.id === payload.center_id)?.name || "nuestro centro auditivo";
          const msg = `Hola ${p.name || ""}, le confirmamos su cita en ${cName} el ${fecha} a las ${hora} (${payload.title}). Si no puede asistir, avísenos por favor. ¡Gracias!`;
          const link = buildWhatsAppLink(p.phone, msg);
          if (link) {
            window.open(link, "_blank", "noopener,noreferrer");
          } else {
            toast.info("Cita creada. El paciente no tiene teléfono válido para WhatsApp.");
          }
        } else if (p) {
          toast.info("Cita creada. El paciente no tiene teléfono registrado.");
        }
      }
      setOpen(false);
      setEditTarget(null);
      setForm({
        center_id: "",
        employee_id: "",
        patient_id: "",
        title: "",
        gabinete: "1",
        start_time: new Date().toISOString().slice(0, 16),
        duration_minutes: 30,
        notes: "",
        status: "programada",
      });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const openEditAppt = (a) => {
    setEditTarget(a);
    setForm({
      center_id: a.center_id || "",
      employee_id: a.employee_id || "",
      patient_id: a.patient_id || "",
      title: a.title || "",
      gabinete: String(a.gabinete || "1"),
      start_time: toLocalInput(a.start_time),
      duration_minutes: a.duration_minutes || 30,
      notes: a.notes || "",
      status: a.status || "programada",
    });
    setOpen(true);
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar cita?")) return;
    try {
      await api.delete(`/appointments/${id}`);
      toast.success("Eliminada");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setStatus = async (apt, status) => {
    try {
      await api.patch(`/appointments/${apt.id}`, { ...apt, status });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openNoShow = (apt) => {
    setNoShowTarget(apt);
    setNoShowReason(apt.no_show_reason || "");
  };

  const confirmNoShow = async () => {
    if (!noShowTarget) return;
    try {
      await api.patch(`/appointments/${noShowTarget.id}`, {
        ...noShowTarget,
        status: "no_show",
        no_show_reason: noShowReason || "",
      });
      toast.success("Marcada como no asistida");
      setNoShowTarget(null);
      setNoShowReason("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const notifyPatient = (apt) => {
    const p = patients.find((x) => x.id === apt.patient_id);
    if (!p) {
      toast.error("Esta cita no tiene paciente asociado");
      return;
    }
    if (!p.phone) {
      toast.error("El paciente no tiene teléfono registrado");
      return;
    }
    const dt = new Date(apt.start_time);
    const fecha = dt.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });
    const hora = dt.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
    const cName = centers.find((c) => c.id === apt.center_id)?.name || "nuestro centro auditivo";
    const msg = `Hola ${p.name || ""}, le recordamos su cita en ${cName} el ${fecha} a las ${hora} (${apt.title}). ¡Le esperamos!`;
    const link = buildWhatsAppLink(p.phone, msg);
    if (!link) {
      toast.error("Teléfono inválido para WhatsApp");
      return;
    }
    window.open(link, "_blank", "noopener,noreferrer");
  };

  const userName = (id) => users.find((u) => u.id === id)?.name || "—";
  const centerName = (id) => centers.find((c) => c.id === id)?.name || "—";
  const patientName = (id) => {
    if (!id) return null;
    const p = patients.find((x) => x.id === id);
    return p ? `${p.name} ${p.surname || ""}`.trim() : null;
  };

  // Group by day
  const days = useMemo(() => {
    const out = [];
    const startWeek = startOfWeek(currentDate);
    for (let i = 0; i < 7; i++) {
      const d = new Date(startWeek);
      d.setDate(d.getDate() + i);
      out.push(d);
    }
    return out;
  }, [currentDate]);

  // ApptCard reutilizable
  const ApptCard = ({ a, compact = false }) => (
    <div
      key={a.id}
      data-testid={`appt-${a.id}`}
      className={`text-xs bg-[#F7F6F3] border-l-2 ${GABINETE_COLOR[a.gabinete] || "border-l-[#6E7772]"} rounded p-2 group`}
    >
      <div className="flex items-center justify-between gap-1">
        <span className="font-medium text-[#2A2F2C]">{timeLabel(a.start_time)}</span>
        <Badge className={STATUS_META[a.status]?.color}>
          {STATUS_META[a.status]?.label}
        </Badge>
      </div>
      <div className="font-medium text-[#2A2F2C] mt-1 truncate">{a.title}</div>
      {!compact && (
        <>
          <div className="text-[#6E7772] mt-0.5 flex items-center gap-1">
            <Clock className="h-3 w-3" /> {a.duration_minutes} min · Gab. {a.gabinete}
          </div>
          <div className="text-[#6E7772] mt-0.5 truncate">{userName(a.employee_id)}</div>
          {patientName(a.patient_id) && (
            <div className="text-[#4A5D4E] text-[11px] mt-0.5 truncate">{patientName(a.patient_id)}</div>
          )}
          {a.status === "no_show" && a.no_show_reason && (
            <div className="text-[#C07F67] text-[11px] mt-0.5 italic truncate" title={a.no_show_reason}>
              Motivo: {a.no_show_reason}
            </div>
          )}
        </>
      )}
      {!isReadOnly && (
        <div className="flex gap-2 mt-2 flex-wrap opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={() => openEditAppt(a)}
            className="text-[10px] text-[#4A5D4E] font-semibold hover:underline"
            data-testid={`appt-edit-${a.id}`}
            title="Cambiar fecha, hora, trabajador o gabinete"
          >
            Editar
          </button>
          <button
            type="button"
            onClick={() => setStatus(a, "completada")}
            className="text-[10px] text-[#4A5D4E] hover:underline"
            data-testid={`appt-attended-${a.id}`}
          >
            Asistió
          </button>
          <button
            type="button"
            onClick={() => openNoShow(a)}
            className="text-[10px] text-[#C07F67] hover:underline"
            data-testid={`appt-noshow-${a.id}`}
          >
            No vino
          </button>
          <button
            type="button"
            onClick={() => setStatus(a, "cancelada")}
            className="text-[10px] text-[#C07F67] hover:underline"
          >
            Cancelar
          </button>
          {a.patient_id && (
            <button
              type="button"
              onClick={() => notifyPatient(a)}
              className="text-[10px] text-[#25D366] hover:underline inline-flex items-center gap-0.5"
              data-testid={`appt-notify-${a.id}`}
              title="Avisar al paciente por WhatsApp"
            >
              <MessageCircle className="h-3 w-3" /> WhatsApp
            </button>
          )}
          <button
            type="button"
            onClick={() => remove(a.id)}
            className="text-[10px] text-[#C07F67] hover:underline ml-auto"
          >
            <Trash2 className="h-3 w-3 inline" />
          </button>
        </div>
      )}
    </div>
  );

  const apptsForDate = (d) => {
    const k = ymd(d);
    return appts
      .filter((a) => a.start_time?.slice(0, 10) === k)
      .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
  };

  // Vista mensual: matriz de semanas
  const monthMatrix = useMemo(() => {
    if (view !== "month") return [];
    const start = startOfMonthGrid(currentDate);
    const end = endOfMonthGrid(currentDate);
    const weeks = [];
    let d = new Date(start);
    while (d <= end) {
      const row = [];
      for (let i = 0; i < 7; i++) {
        row.push(new Date(d));
        d.setDate(d.getDate() + 1);
      }
      weeks.push(row);
    }
    return weeks;
  }, [view, currentDate]);

  return (
    <div className="space-y-6" data-testid="appointments-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#6E7772] font-semibold">
            Agenda
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#2A2F2C] mt-1">
            Calendario de citas
          </h1>
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          {["admin", "center_admin"].includes(user?.role) && (
            <div className="w-48">
              <Select value={centerFilter} onValueChange={setCenterFilter}>
                <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los centros</SelectItem>
                  {centers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Tabs value={view} onValueChange={setView}>
            <TabsList className="bg-[#EAE6DF]">
              <TabsTrigger value="day" data-testid="view-day">Día</TabsTrigger>
              <TabsTrigger value="cabinets" data-testid="view-cabinets">Gabinetes</TabsTrigger>
              <TabsTrigger value="week" data-testid="view-week">Semana</TabsTrigger>
              <TabsTrigger value="month" data-testid="view-month">Mes</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={goPrev} data-testid="cal-prev">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="font-heading text-sm text-[#2A2F2C] min-w-[180px] text-center capitalize">
              {periodLabel}
            </div>
            <Button variant="outline" size="sm" onClick={goNext} data-testid="cal-next">
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={goToday} data-testid="cal-today">
              Hoy
            </Button>
          </div>

          {!isReadOnly && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditTarget(null); }}>
            <DialogTrigger asChild>
              <Button
                data-testid="new-appointment-btn"
                className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
                onClick={() => {
                  setEditTarget(null);
                  setForm({
                    center_id: "",
                    employee_id: "",
                    patient_id: "",
                    title: "",
                    gabinete: "1",
                    start_time: new Date().toISOString().slice(0, 16),
                    duration_minutes: 30,
                    notes: "",
                    status: "programada",
                  });
                }}
              >
                <Plus className="h-4 w-4 mr-2" /> Nueva cita
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl bg-white">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">{editTarget ? "Editar cita" : "Nueva cita"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Propuesta / título *</Label>
                  <Input
                    data-testid="appt-title"
                    required
                    placeholder="Ej. Revisión audífonos, primera visita…"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Centro *</Label>
                  {["admin", "center_admin"].includes(user?.role) ? (
                    <Select value={form.center_id} onValueChange={(v) => setForm({ ...form, center_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Selecciona" /></SelectTrigger>
                      <SelectContent>
                        {centers.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input value={centerName(user?.center_id)} disabled />
                  )}
                </div>
                <div>
                  <Label>Trabajador *</Label>
                  <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                    <SelectTrigger data-testid="appt-employee"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                    <SelectContent>
                      {users
                        .filter((u) => u.active !== false)
                        .map((u) => {
                          const busy = avail?.busy_employees?.includes(u.id);
                          const off = avail?.off_schedule_employees?.includes(u.id);
                          return (
                            <SelectItem key={u.id} value={u.id} disabled={!!(busy || off)}>
                              {u.name} {u.role ? `· ${u.role}` : ""}{busy ? " — ocupado" : off ? " — fuera de jornada" : ""}
                            </SelectItem>
                          );
                        })}
                    </SelectContent>
                  </Select>
                  {avail && form.employee_id && avail.busy_employees?.includes(form.employee_id) && (
                    <p className="text-[11px] text-red-600 mt-1" data-testid="appt-employee-busy-warning">
                      Este trabajador ya tiene otra cita en ese horario.
                    </p>
                  )}
                  {avail && form.employee_id && !avail.busy_employees?.includes(form.employee_id) && avail.off_schedule_employees?.includes(form.employee_id) && (
                    <p className="text-[11px] text-red-600 mt-1" data-testid="appt-employee-schedule-warning">
                      Este trabajador está fuera de su jornada laboral en ese horario.
                    </p>
                  )}
                </div>
                <div>
                  <Label>Gabinete</Label>
                  <Select value={form.gabinete} onValueChange={(v) => setForm({ ...form, gabinete: v })}>
                    <SelectTrigger data-testid="appt-gabinete"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["1", "2"].map((g) => {
                        const busy = avail?.busy_gabinetes?.includes(g);
                        return (
                          <SelectItem key={g} value={g} disabled={!!busy}>
                            Gabinete {g}{busy ? " — ocupado" : ""}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  {avail && avail.busy_gabinetes?.includes(form.gabinete) && (
                    <p className="text-[11px] text-red-600 mt-1" data-testid="appt-gabinete-busy-warning">
                      Este gabinete ya está ocupado en ese horario.
                    </p>
                  )}
                </div>
                <div>
                  <Label>Paciente (opcional)</Label>
                  <PatientCombobox
                    patients={patients}
                    value={form.patient_id || ""}
                    onChange={(id) => setForm({ ...form, patient_id: id || "" })}
                    placeholder="Buscar paciente por nombre, DNI o teléfono…"
                    testid="appt-patient"
                  />
                </div>
                <div>
                  <Label>Fecha y hora *</Label>
                  <Input
                    data-testid="appt-start"
                    type="datetime-local"
                    required
                    value={form.start_time}
                    onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Duración estimada (min) *</Label>
                  <Input
                    data-testid="appt-duration"
                    type="number"
                    required
                    min={5}
                    step={5}
                    value={form.duration_minutes}
                    onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Notas</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
                <div className="md:col-span-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={notifyAfterCreate}
                      onChange={(e) => setNotifyAfterCreate(e.target.checked)}
                      data-testid="appt-notify-checkbox"
                      className="h-4 w-4"
                    />
                    Notificar al paciente por WhatsApp al guardar (abre la conversación)
                  </label>
                </div>
                <DialogFooter className="md:col-span-2">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    data-testid="save-appointment-btn"
                    className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
                  >
                    {saving ? "Guardando…" : editTarget ? "Guardar cambios" : "Crear cita"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {/* Calendar views */}
      {view === "week" && (
        <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
          {days.map((d) => {
            const dayKey = ymd(d);
            const dayAppts = apptsForDate(d);
            const isToday = ymd(new Date()) === dayKey;
            return (
              <div
                key={dayKey}
                className={`rounded-lg border bg-white p-3 min-h-[140px] ${
                  isToday ? "border-[#4A5D4E]" : "border-[#E5E3DB]"
                }`}
              >
                <div className="text-xs tracking-wider uppercase font-semibold text-[#6E7772]">
                  {d.toLocaleDateString("es-ES", { weekday: "long" })}
                </div>
                <div
                  className={`font-heading text-2xl ${isToday ? "text-[#4A5D4E]" : "text-[#2A2F2C]"}`}
                >
                  {d.getDate()}
                </div>
                <div className="mt-3 space-y-2">
                  {dayAppts.length === 0 && (
                    <div className="text-xs text-[#6E7772] italic">Sin citas</div>
                  )}
                  {dayAppts.map((a) => (
                    <ApptCard key={a.id} a={a} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {view === "day" && (
        <div className="rounded-lg border bg-white border-[#E5E3DB] p-6 min-h-[300px]" data-testid="day-view">
          <div className="text-xs tracking-wider uppercase font-semibold text-[#6E7772] mb-2">
            {currentDate.toLocaleDateString("es-ES", { weekday: "long" })}
          </div>
          <div className="font-heading text-4xl text-[#2A2F2C] mb-6">
            {currentDate.getDate()}
          </div>
          {(() => {
            const dayAppts = apptsForDate(currentDate);
            if (dayAppts.length === 0) {
              return (
                <div className="text-sm text-[#6E7772] italic">
                  No hay citas para este día.
                </div>
              );
            }
            // Agrupar por gabinete
            const gabs = { "1": [], "2": [] };
            dayAppts.forEach((a) => {
              (gabs[a.gabinete || "1"] || gabs["1"]).push(a);
            });
            return (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {["1", "2"].map((g) => (
                  <div key={g}>
                    <div className="text-xs uppercase tracking-wider font-semibold text-[#6E7772] mb-2">
                      Gabinete {g}
                    </div>
                    <div className="space-y-2">
                      {gabs[g].length === 0 ? (
                        <div className="text-xs text-[#6E7772] italic">Libre todo el día</div>
                      ) : (
                        gabs[g].map((a) => <ApptCard key={a.id} a={a} />)
                      )}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {view === "cabinets" && (() => {
        const START_MIN = 9 * 60;
        const END_MIN = 20 * 60;
        const STEP = 30;
        const slots = [];
        for (let m = START_MIN; m < END_MIN; m += STEP) slots.push(m);
        const rowsCount = slots.length;
        const dayAppts = apptsForDate(currentDate).filter((a) => a.status !== "cancelada");
        const localMinutes = (iso) => {
          const d = new Date(iso);
          return d.getHours() * 60 + d.getMinutes();
        };
        const fmt = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
        const openNewAt = (minutes, gab) => {
          const d = new Date(currentDate);
          d.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
          setEditTarget(null);
          setForm({
            center_id: "",
            employee_id: "",
            patient_id: "",
            title: "",
            gabinete: gab,
            start_time: toLocalInput(d.toISOString()),
            duration_minutes: 30,
            notes: "",
            status: "programada",
          });
          setOpen(true);
        };
        const gridCols = { gridTemplateColumns: "64px repeat(2, minmax(220px, 1fr))" };
        return (
          <div className="rounded-lg border bg-white border-[#E5E3DB] p-4 overflow-x-auto" data-testid="cabinets-view">
            <div className="grid" style={gridCols}>
              <div />
              {["1", "2"].map((g) => (
                <div
                  key={g}
                  className={`text-center text-xs uppercase tracking-wider font-semibold text-[#6E7772] pb-2 border-b-2 ${
                    g === "1" ? "border-[#4A5D4E]" : "border-[#C07F67]"
                  }`}
                >
                  Gabinete {g}
                </div>
              ))}
            </div>
            <div className="grid" style={{ ...gridCols, gridAutoRows: "2.75rem" }}>
              {slots.map((m, i) => (
                <div
                  key={`h-${m}`}
                  className="text-[11px] text-[#6E7772] pr-2 text-right border-t border-[#F0EEE6]"
                  style={{ gridColumn: 1, gridRow: i + 1 }}
                >
                  {m % 60 === 0 ? fmt(m) : ""}
                </div>
              ))}
              {["1", "2"].map((g, gi) =>
                slots.map((m, i) => (
                  <button
                    key={`${g}-${m}`}
                    type="button"
                    onClick={() => !isReadOnly && openNewAt(m, g)}
                    className="group border-t border-l border-[#F0EEE6] hover:bg-[#F4F7F4] transition-colors text-left"
                    style={{ gridColumn: gi + 2, gridRow: i + 1 }}
                    data-testid={`slot-${g}-${fmt(m)}`}
                    title={`Crear cita a las ${fmt(m)} en gabinete ${g}`}
                  >
                    <span className="opacity-0 group-hover:opacity-100 text-[10px] text-[#4A5D4E] pl-1">
                      + {fmt(m)}
                    </span>
                  </button>
                )),
              )}
              {dayAppts.map((a) => {
                const g = String(a.gabinete || "1");
                const gi = g === "2" ? 1 : 0;
                const s = localMinutes(a.start_time);
                const e = s + (a.duration_minutes || 30);
                if (e <= START_MIN || s >= END_MIN) return null;
                const rowStart = Math.max(1, Math.floor((s - START_MIN) / STEP) + 1);
                const rowEnd = Math.max(
                  rowStart + 1,
                  Math.min(rowsCount + 1, Math.ceil((Math.min(e, END_MIN) - START_MIN) / STEP) + 1),
                );
                return (
                  <div
                    key={a.id}
                    className={`m-0.5 rounded-md px-2 py-1 text-xs overflow-hidden border-l-4 ${
                      GABINETE_COLOR[g] || "border-l-[#6E7772]"
                    } ${STATUS_META[a.status]?.color || "bg-[#EAE6DF]"} shadow-sm cursor-pointer hover:opacity-90`}
                    style={{ gridColumn: gi + 2, gridRow: `${rowStart} / ${rowEnd}`, zIndex: 2 }}
                    onClick={() => !isReadOnly && openEditAppt(a)}
                    data-testid={`cab-appt-${a.id}`}
                    title="Haz clic para editar esta cita"
                  >
                    <div className="font-semibold truncate">
                      {timeLabel(a.start_time)} · {a.title || "Cita"}
                    </div>
                    <div className="truncate text-[11px]">
                      {patientName(a.patient_id) || userName(a.employee_id)}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] text-[#6E7772] mt-2">
              Haz clic en un hueco libre para crear una cita a esa hora en ese gabinete · haz clic en una cita para editarla. Citas fuera de 09:00–20:00 se muestran en las vistas Día/Semana.
            </p>
          </div>
        );
      })()}

      {view === "month" && (
        <div className="rounded-lg border bg-white border-[#E5E3DB] overflow-hidden" data-testid="month-view">
          <div className="grid grid-cols-7 bg-[#F7F6F3] border-b border-[#E5E3DB]">
            {["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map((d) => (
              <div key={d} className="px-2 py-2 text-xs uppercase tracking-wider font-semibold text-[#6E7772] text-center">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {monthMatrix.flat().map((d, idx) => {
              const dayKey = ymd(d);
              const dayAppts = apptsForDate(d);
              const isToday = ymd(new Date()) === dayKey;
              const inMonth = d.getMonth() === currentDate.getMonth();
              return (
                <button
                  type="button"
                  key={`${dayKey}-${idx}`}
                  onClick={() => {
                    setCurrentDate(d);
                    setView("day");
                  }}
                  className={`border-r border-b border-[#E5E3DB] min-h-[110px] p-2 text-left transition-colors hover:bg-[#FCFBF8] ${
                    !inMonth ? "bg-[#FAFAF7]" : "bg-white"
                  } ${isToday ? "ring-1 ring-inset ring-[#4A5D4E]" : ""}`}
                  data-testid={`month-cell-${dayKey}`}
                >
                  <div className={`text-xs font-semibold ${isToday ? "text-[#4A5D4E]" : inMonth ? "text-[#2A2F2C]" : "text-[#B4B3AB]"}`}>
                    {d.getDate()}
                  </div>
                  <div className="mt-1 space-y-0.5">
                    {dayAppts.slice(0, 3).map((a) => (
                      <div
                        key={a.id}
                        className={`text-[10px] truncate border-l-2 ${GABINETE_COLOR[a.gabinete] || "border-l-[#6E7772]"} bg-[#F7F6F3] rounded px-1`}
                        title={`${timeLabel(a.start_time)} · ${a.title}`}
                      >
                        {timeLabel(a.start_time)} {a.title}
                      </div>
                    ))}
                    {dayAppts.length > 3 && (
                      <div className="text-[10px] text-[#6E7772] italic">+{dayAppts.length - 3} más</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <Card className="bg-white border-[#E5E3DB] shadow-none">
        <CardContent className="p-4 flex flex-wrap items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="inline-block h-3 w-3 rounded-sm bg-[#4A5D4E]" /> Gabinete 1
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block h-3 w-3 rounded-sm bg-[#C07F67]" /> Gabinete 2
          </div>
        </CardContent>
      </Card>

      {/* Diálogo motivo no-show */}
      <Dialog open={!!noShowTarget} onOpenChange={(v) => { if (!v) { setNoShowTarget(null); setNoShowReason(""); } }}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Paciente no asistió</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#6E7772]">
              Indica el motivo (opcional) para llevar registro de inasistencias.
            </p>
            <Textarea
              data-testid="no-show-reason-input"
              placeholder="Ej. No avisó, llamada perdida, paciente avisó al momento, traslado…"
              value={noShowReason}
              onChange={(e) => setNoShowReason(e.target.value)}
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => { setNoShowTarget(null); setNoShowReason(""); }}>
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={confirmNoShow}
              data-testid="confirm-no-show-btn"
              className="bg-[#C07F67] hover:bg-[#A86952] text-white"
            >
              Marcar como no vino
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
